// HR-domain (leave) HTTP service (Layer 2 — transport). Wraps lib/http.ts's
// request() for the Module 1 leave endpoints used by pages/Requests.tsx and
// pages/Approvals.tsx. Methods moved verbatim off ApiClient
// (apps/frontend/src/utils/api.ts), which now delegates here to preserve
// every existing `apiClient.*` call site.
import { request, API_BASE_URL } from '@/lib/http'
import { runAuthenticatedDownload } from '@/lib/downloads'
import type {
  LeaveRequest, ApprovalDecision, EmployeeDocument, EmployeeDocType,
  LeaveBalance, LeaveBalancesResponse, LeaveEligibility, LeavePolicy,
  LeaveCalendarEvent, LeaveOnLeaveCount, LeavePolicyPayload,
} from '@/types'

export const hrApi = {
  getMyLeave(status?: string): Promise<{ leave_requests: LeaveRequest[] }> {
    return request(`/hr/leave/mine${status ? `?status=${status}` : ''}`)
  },

  createLeave(payload: {
    team_id: number; leave_type: string; start_date: string; end_date: string; days_count?: number
    reason?: string; handover_notes: string
  }): Promise<LeaveRequest> {
    return request('/hr/leave', { method: 'POST', body: JSON.stringify(payload) })
  },

  getLeavePolicies(employmentType?: string, includePast = false): Promise<{ policies: LeavePolicy[] }> {
    const qs = new URLSearchParams()
    if (employmentType) qs.set('employment_type', employmentType)
    if (includePast) qs.set('include_past', '1')
    const query = qs.toString()
    return request<{ policies: LeavePolicy[] }>(`/hr/leave/policies${query ? `?${query}` : ''}`)
  },

  createLeavePolicy(payload: LeavePolicyPayload): Promise<LeavePolicy> {
    return request<LeavePolicy>('/hr/leave/policies', { method: 'POST', body: JSON.stringify(payload) })
  },

  updateLeavePolicy(id: number, payload: LeavePolicyPayload): Promise<LeavePolicy> {
    return request<LeavePolicy>(`/hr/leave/policies/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })
  },

  supersedeLeavePolicy(id: number, payload: LeavePolicyPayload): Promise<LeavePolicy> {
    return request<LeavePolicy>(`/hr/leave/policies/${id}/supersede`, { method: 'POST', body: JSON.stringify(payload) })
  },

  getMyLeaveBalances(year?: number, asOf?: string): Promise<LeaveBalancesResponse> {
    const qs = new URLSearchParams()
    if (year != null) qs.set('year', String(year))
    if (asOf) qs.set('as_of', asOf)
    const query = qs.toString()
    return request<LeaveBalancesResponse>(`/hr/leave/balances${query ? `?${query}` : ''}`)
  },

  getEmployeeLeaveBalances(userId: number, year?: number, asOf?: string): Promise<LeaveBalancesResponse> {
    const qs = new URLSearchParams()
    if (year != null) qs.set('year', String(year))
    if (asOf) qs.set('as_of', asOf)
    const query = qs.toString()
    return request<LeaveBalancesResponse>(`/hr/employees/${userId}/leave/balances${query ? `?${query}` : ''}`)
  },

  addLeaveAdjustment(userId: number, year: number, leaveType: string, payload: { days: string; note: string }): Promise<LeaveBalance> {
    return request<LeaveBalance>(`/hr/employees/${userId}/leave/balances/${year}/${encodeURIComponent(leaveType)}/adjustments`, {
      method: 'POST', body: JSON.stringify(payload),
    })
  },

  getLeaveEligibility(startDate: string, endDate: string): Promise<LeaveEligibility> {
    const qs = new URLSearchParams({ start_date: startDate, end_date: endDate })
    return request<LeaveEligibility>(`/hr/leave/eligibility?${qs.toString()}`)
  },

  getLeaveCalendar(from: string, to: string, teamId?: number): Promise<{ from: string; to: string; items: LeaveCalendarEvent[] }> {
    const qs = new URLSearchParams({ from, to })
    if (teamId != null) qs.set('team_id', String(teamId))
    return request<{ from: string; to: string; items: LeaveCalendarEvent[] }>(`/hr/leave/calendar?${qs.toString()}`)
  },

  getOnLeaveCount(on: string): Promise<LeaveOnLeaveCount> {
    return request<LeaveOnLeaveCount>(`/hr/leave/on-leave-count?on=${encodeURIComponent(on)}`)
  },

  getTeamLeave(teamId: number, status?: string): Promise<{ leave_requests: LeaveRequest[] }> {
    return request(`/hr/leave/team/${teamId}${status ? `?status=${status}` : ''}`)
  },

  getLeaveApprovals(): Promise<{ leave_requests: LeaveRequest[] }> {
    return request('/hr/leave/approvals')
  },

  decideLeave(id: number, status: ApprovalDecision, notes?: string): Promise<LeaveRequest> {
    return request(`/hr/leave/${id}/decision`, { method: 'POST', body: JSON.stringify({ status, notes }) })
  },

  // ── Employee documents (plan 022 / A4) ─────────────────────────────────
  presignEmployeeDocument(
    userId: number,
    meta: { file_name: string; content_type: string; size_bytes: number },
  ): Promise<{ upload_url: string; file_key: string; storage_backend: string }> {
    return request(`/hr/employees/${userId}/documents/presign`, { method: 'POST', body: JSON.stringify(meta) })
  },

  confirmEmployeeDocument(
    userId: number,
    payload: {
      file_key: string; file_name: string; content_type: string; size_bytes: number
      doc_type: EmployeeDocType; title?: string; issued_on?: string; expires_on?: string; notes?: string
    },
  ): Promise<EmployeeDocument> {
    return request(`/hr/employees/${userId}/documents`, { method: 'POST', body: JSON.stringify(payload) })
  },

  listEmployeeDocuments(userId: number): Promise<{ items: EmployeeDocument[] }> {
    return request(`/hr/employees/${userId}/documents`)
  },

  updateEmployeeDocument(
    docId: number,
    patch: Partial<Pick<EmployeeDocument, 'doc_type' | 'title' | 'issued_on' | 'expires_on' | 'notes'>>,
  ): Promise<EmployeeDocument> {
    return request(`/hr/documents/${docId}`, { method: 'PATCH', body: JSON.stringify(patch) })
  },

  deleteEmployeeDocument(docId: number): Promise<void> {
    return request(`/hr/documents/${docId}`, { method: 'DELETE' })
  },

  downloadEmployeeDocument(docId: number, fileName: string): Promise<void> {
    return runAuthenticatedDownload(`${API_BASE_URL}/hr/documents/${docId}/download`, fileName)
  },
}
